import type { FunctionComponent } from 'react';
import { useEffect } from 'react';

import { isMemberLoggedIn } from '@trello/authentication';
import { forTemplate } from '@trello/legacy-i18n';
import { navigate } from '@trello/router/navigate';
import { escapeReturnUrl } from '@trello/urls';

import { useDocumentTitle } from 'app/src/components/DocumentTitle';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { useAppManagementQuery } from './AppManagementQuery.generated';
import { CreatePowerUpPage } from './CreatePowerUpPage';
import { DeveloperAgreement } from './DeveloperAgreement';
import { PowerUpPage } from './PowerUpPage';
import { PowerUpsPage } from './PowerUpsPage';

import styles from './AppManagement.less';

const format = forTemplate('app_management');

interface AppManagementProps {
  section?: string | null;
  subsection?: string | null;
}

export const AppManagement: FunctionComponent<AppManagementProps> = ({
  section,
  subsection,
}) => {
  useDocumentTitle(format('power-ups-administration'));

  useEffect(() => {
    // eslint-disable-next-line @trello/no-query-selector
    const contentContainer = document.querySelector('#content');
    if (contentContainer) {
      contentContainer.scrollTop = 0;
    }
  }, [section, subsection]);

  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, error, loading, refetch } = useAppManagementQuery({
    waitOn: ['None'],
  });

  if (!isMemberLoggedIn()) {
    const returnUrl = escapeReturnUrl(location.href);
    window.location.href = `/login?returnUrl=${returnUrl}`;

    return null;
  }

  if (loading) {
    return <LoadingSpinner className={styles.body} />;
  }

  const member = data?.member;

  if (error || !member) {
    return (
      <div className={styles.content}>{format('something-went-wrong')}</div>
    );
  }

  let pageContent;

  if (
    !member.agreements.some(
      (agreement) => agreement.agreementType === 'developer-terms',
    )
  ) {
    pageContent = <DeveloperAgreement refetchAgreement={refetch} />;
  } else {
    if (typeof section === 'undefined' || section === null) {
      // /power-ups/admin
      pageContent = <PowerUpsPage />;
    } else if (section && /^[a-fA-F0-9]{24}$/.test(section)) {
      // /power-ups/admin/:idPlugin
      pageContent = <PowerUpPage idPlugin={section} subsection={subsection} />;
    } else if (section === 'new') {
      pageContent = <CreatePowerUpPage />;
    } else {
      navigate('/power-ups/admin', { trigger: true });
    }
  }

  return (
    <div className={styles.body}>
      <div className={styles.navigationAndContent}>{pageContent}</div>
    </div>
  );
};
